
/** Millimetres of world per CSS pixel. Larger is further out. 12 puts a
 *  character at 145 px, which is as close as the interest radius allows before
 *  monsters the server is already sending sit off-screen. */
export const MIN_MM_PER_PIXEL = 12;

/** 96 mm/px shows about 123 m across a 1280 px window -- close to the map's own
 *  `ViewRadius 128`, and the point past which a player is a few pixels. */
export const MAX_MM_PER_PIXEL = 96;

/** About 26 m across a 1280 px window, which puts a 1.74 m character at 87 px --
 *  roughly an eighth of an 800 px window, the proportion the original's own
 *  camera holds a player at. Five notches out still reaches the old 40, so the
 *  town square is a scroll away rather than gone. */
export const DEFAULT_MM_PER_PIXEL = 20;

/** One notch. 1.15 is roughly eighteen steps end to end, which is a couple of
 *  wheel flicks rather than a scroll. */
const STEP = 1.15;

const STORAGE_KEY = "metin3.zoom";

export function clampZoom(mmPerPixel: number): number {
  if (!Number.isFinite(mmPerPixel)) return DEFAULT_MM_PER_PIXEL;
  return Math.max(MIN_MM_PER_PIXEL, Math.min(MAX_MM_PER_PIXEL, mmPerPixel));
}

export function zoomBy(mmPerPixel: number, notches: number): number {
  return clampZoom(mmPerPixel * Math.pow(STEP, notches));
}

/** Read the stored zoom, or the default. Never throws. */
export function loadZoom(storage: Storage | null = safeStorage()): number {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) return DEFAULT_MM_PER_PIXEL;
    return clampZoom(Number(raw));
  } catch {
    return DEFAULT_MM_PER_PIXEL;
  }
}

/** Remember a zoom. Never throws; a browser that refuses storage just forgets. */
export function saveZoom(mmPerPixel: number, storage: Storage | null = safeStorage()): void {
  try {
    // Rounded: a geometric step lands on 40.00000000000001, and writing
    // seventeen significant digits to storage is noise in every debug dump that
    // ever reads it back.
    storage?.setItem(STORAGE_KEY, clampZoom(mmPerPixel).toFixed(2));
  } catch {
    // Private browsing, a full quota, a blocked origin. None of them is worth a
    // console warning on every notch of the wheel.
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** What a zoom control drives. `Camera` satisfies it. */
export interface Zoomable {
  mmPerPixel: number;
  /** Optional renderer-owned presentation cap, e.g. the source camera mode. */
  clampZoom?(mmPerPixel: number): number;
}

export class ZoomControl {
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinchStartDistance = 0;
  private pinchStartScale = 0;

  constructor(
    private readonly target: Zoomable,
    element: HTMLElement,
    private readonly onChange: (mmPerPixel: number) => void = saveZoom,
  ) {
    element.addEventListener(
      "wheel",
      (event) => {
        // Without this the page scrolls behind the canvas on a trackpad, which
        // on mobile Safari is also how the address bar comes back mid-fight.
        event.preventDefault();
        this.apply(zoomBy(this.target.mmPerPixel, Math.sign(event.deltaY)));
      },
      { passive: false },
    );

    element.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "touch") return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 2) {
        this.pinchStartDistance = this.spread();
        this.pinchStartScale = this.target.mmPerPixel;
      }
    });

    element.addEventListener("pointermove", (event) => {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size !== 2 || this.pinchStartDistance <= 0) return;
      const ratio = this.spread() / this.pinchStartDistance;
      if (ratio <= 0) return;
      // Fingers apart zooms in, so the scale goes down as the spread goes up.
      this.apply(clampZoom(this.pinchStartScale / ratio));
    });

    const release = (event: PointerEvent): void => {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinchStartDistance = 0;
    };
    element.addEventListener("pointerup", release);
    element.addEventListener("pointercancel", release);
  }

  /** True while two fingers are down. Steering should stand down. */
  get pinching(): boolean {
    return this.pointers.size >= 2;
  }

  private spread(): number {
    const [a, b] = [...this.pointers.values()];
    if (a === undefined || b === undefined) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  private apply(mmPerPixel: number): void {
    const clamped = this.target.clampZoom?.(mmPerPixel) ?? mmPerPixel;
    if (clamped === this.target.mmPerPixel) return;
    this.target.mmPerPixel = clamped;
    this.onChange(clamped);
  }
}
